/* jshint esversion: 6 */
import React, { useEffect, useRef, useState } from 'react';
import * as _ from 'underscore';
import { SUB_PREVIEW_ADDONS } from 'app/redux/constants.js';
import AddOnChangeDialog from '@/shadcn/components/account/PlanChange/AddOnChangeDialog.tsx';
import useChangePreview, { useConfirmationStep } from './useChangePreview.js';

/*
 * Opens through the legacy modal stack (addModal / closeOtherModals) to confirm an
 * add-on pack change.
 *
 * Callers pass the add-on, the new pack count and what confirming and cancelling do,
 * along with their own props: `calculateAddOns` comes from the caller's PricingModule,
 * while App re-renders the stack with live `app` / `plans` / `user`. On mount it asks
 * for a preview of the change, which is what the dialog's "Due today" and button show.
 *
 * Off the plan page nothing else closes it, so once the change lands it refreshes the
 * user and their perms and closes when both are back.
 */
export default function AddOnChangeConfirm(props) {
    const { addOn, quantity, app, plans, user } = props,
        constants = app.config.constants,
        subscription = _.isEmpty(plans.current) ? user.plan : plans.current,
        planDetails = plans.details[subscription.planLevel] || {},
        addOnId = addOn === 'add_on_social' ? constants.SUBSCRIPTION_ADDON_SOCIALS : constants.SUBSCRIPTION_ADDON_POSTS_1,
        committed = parseInt(subscription.addOns?.[addOn]?.qty || 0, 10),
        currentLimits = props.calculateAddOns(),
        nextLimits = props.calculateAddOns([{ addOn: addOnId, quantity }]),
        postsPerPack = planDetails.addOnQuantity?.[constants.SUBSCRIPTION_ADDON_POSTS_1]?.posts || 0,
        post = { [addOn]: quantity },
        updating = app.loading.SUB_ADDON_UPDATE;

    const [refreshing, setRefreshing] = useState(false),
        wasUpdating = useRef(false),
        sawRefresh = useRef(false),
        preview = useChangePreview(props, SUB_PREVIEW_ADDONS, post);

    useConfirmationStep();

    useEffect(() => {
        if (wasUpdating.current && !updating) {
            if (props.getApiErrors(['SUB_ADDON_UPDATE']).length) {
                props.closeAllModals();
            } else {
                props.getUserPerms();
                props.getUser();
                setRefreshing(true);
            }
        }
        wasUpdating.current = updating;
    }, [updating]);

    useEffect(() => {
        if (!refreshing) {
            return;
        }
        if (app.loading.USER_FETCH || app.loading.USER_FETCH_PERMS) {
            sawRefresh.current = true;
        } else if (sawRefresh.current) {
            props.closeAllModals();
        }
    }, [refreshing, app.loading.USER_FETCH, app.loading.USER_FETCH_PERMS]);

    const close = () => {
        if (props.onCancel) {
            props.onCancel();
        }
        props.closeAllModals();
    };

    return (
        <AddOnChangeDialog
            addOn={addOn}
            packs={{ from: committed, to: quantity }}
            units={addOn === 'add_on_social'
                ? { from: currentLimits.outputs, to: nextLimits.outputs }
                : { from: committed * postsPerPack, to: quantity * postsPerPack }}
            prices={{ from: Number(currentLimits.price), to: Number(nextLimits.price) }}
            annual={parseInt(subscription.planTerm, 10) === constants.SUBSCRIPTION_TERM_ANNUAL}
            nextBilling={subscription.nextBilling}
            preview={preview}
            busy={updating}
            onConfirm={props.onConfirm}
            onCancel={close}
            onClose={close} />
    );
}
